/**
 * Copyright © 2026 Rémi Pace.
 * This file is part of Abc-Map.
 *
 * Abc-Map is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of
 * the License, or (at your option) any later version.
 *
 * Abc-Map is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General
 * Public License along with Abc-Map. If not, see <https://www.gnu.org/licenses/>.
 */

import type { HistoryKey } from '../../core/history/HistoryKey.ts';
import type { InformationKey, ServiceWorkerUiState } from './state.ts';

export enum ActionType {
  SetHistoryCapabilities = 'Ui-SetHistoryCapabilities',
  CleanHistoryCapabilities = 'Ui-CleanHistoryCapabilities',
  SetSideMenuState = 'Ui-SetSideMenuState',
  AckInformation = 'Ui-AckInformation',
  SetExperimentalFeature = 'Ui-SetExperimentalFeature',
  IncrementVisitCounter = 'Ui-IncrementVisitCounter',
  RegisterModuleUsage = 'Ui-RegisterModuleUsage',
  MarkFavorite = 'Ui-MarkFavorite',
  RestoreDefaultFavoriteModules = 'Ui-RestoreDefaultFavoriteModules',
  SetServiceWorkerState = 'Ui-SetServiceWorkerState',
}

export interface SetHistoryCapabilities {
  type: ActionType.SetHistoryCapabilities;
  key: HistoryKey;
  canUndo: boolean;
  canRedo: boolean;
}

export interface CleanHistoryCapabilities {
  type: ActionType.CleanHistoryCapabilities;
}

export interface SetSideMenuState {
  type: ActionType.SetSideMenuState;
  menuId: string;
  state: boolean;
}

export interface AckInformation {
  type: ActionType.AckInformation;
  name: InformationKey;
}

export interface SetExperimentalFeature {
  type: ActionType.SetExperimentalFeature;
  id: string;
  state: boolean;
}

export interface IncrementVisitCounter {
  type: ActionType.IncrementVisitCounter;
}

export interface RegisterModuleUsage {
  type: ActionType.RegisterModuleUsage;
  moduleId: string;
}

export interface MarkFavorite {
  type: ActionType.MarkFavorite;
  moduleId: string;
  favorite: boolean;
}

export interface RestoreDefaultFavoriteModules {
  type: ActionType.RestoreDefaultFavoriteModules;
}

export interface SetServiceWorkerState {
  type: ActionType.SetServiceWorkerState;
  state: Partial<ServiceWorkerUiState>;
}

export type UiAction =
  | SetHistoryCapabilities
  | CleanHistoryCapabilities
  | SetSideMenuState
  | AckInformation
  | IncrementVisitCounter
  | SetExperimentalFeature
  | RegisterModuleUsage
  | MarkFavorite
  | RestoreDefaultFavoriteModules
  | SetServiceWorkerState;

export class UiActions {
  public static setHistoryCapabilities(key: HistoryKey, canUndo: boolean, canRedo: boolean): UiAction {
    return {
      type: ActionType.SetHistoryCapabilities,
      key,
      canUndo,
      canRedo,
    };
  }

  public static cleanHistoryCapabilities(): UiAction {
    return {
      type: ActionType.CleanHistoryCapabilities,
    };
  }

  public static setSideMenuState(menuId: string, state: boolean): UiAction {
    return {
      type: ActionType.SetSideMenuState,
      menuId,
      state,
    };
  }

  public static ackInformation(name: InformationKey): UiAction {
    return {
      type: ActionType.AckInformation,
      name,
    };
  }

  public static setExperimentalFeature(id: string, state: boolean): UiAction {
    return {
      type: ActionType.SetExperimentalFeature,
      id,
      state,
    };
  }

  public static incrementVisitCounter(): UiAction {
    return {
      type: ActionType.IncrementVisitCounter,
    };
  }

  public static registerModuleUsage(moduleId: string): UiAction {
    return {
      type: ActionType.RegisterModuleUsage,
      moduleId,
    };
  }

  public static markFavorite(moduleId: string, favorite: boolean): UiAction {
    return {
      type: ActionType.MarkFavorite,
      moduleId,
      favorite,
    };
  }

  public static restoreDefaultFavoriteModules(): UiAction {
    return {
      type: ActionType.RestoreDefaultFavoriteModules,
    };
  }

  public static setServiceWorkerState(state: Partial<ServiceWorkerUiState>): UiAction {
    return {
      type: ActionType.SetServiceWorkerState,
      state,
    };
  }
}
